import React, { useEffect, useState } from 'react';

export interface DayEditModalProps {
  isOpen: boolean;
  date: string;
  isNew?: boolean;
  initialHours: number;
  initialStatus: string;
  onSave: (date: string, hours: number, status: string) => void;
  onDelete: (date: string) => void;
  onClose: () => void;
}

const DayEditModal: React.FC<DayEditModalProps> = ({
  isOpen,
  date,
  isNew = false,
  initialHours,
  initialStatus,
  onSave,
  onDelete,
  onClose,
}) => {
  const [selectedDate, setSelectedDate] = useState(date);
  const [hours, setHours] = useState(initialHours.toString());
  const [status, setStatus] = useState(initialStatus);
  // Handle Escape key close
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    const parsedHours = parseFloat(hours) || 0;
    onSave(selectedDate, parsedHours, status.trim());
  };

  const handleDelete = () => {
    if (confirm('Are you sure you want to clear/delete this entry?')) {
      onDelete(selectedDate);
    }
  };

  const quickStatuses = ['Present', 'Sick Leave', 'Vacation Leave', 'Holiday', 'Absent'];

  return (
    <div
      onClick={onClose}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/60 backdrop-blur-sm p-4 animate-fade-in"
    >
      <div
        onClick={(e) => e.stopPropagation()}
        className="w-full max-w-md bg-card-bg border border-card-border rounded-lg p-6 relative overflow-hidden shadow-2xl animate-scale-up"
      >
        {/* Top accent line */}
        <div className="absolute top-0 left-0 right-0 h-[3px] bg-accent"></div>

        {/* Header */}
        <div className="flex justify-between items-start mb-5">
          <div>
            <h3 className="text-md font-display font-semibold tracking-tight text-foreground uppercase">
              {isNew ? 'New Manual Entry' : 'Edit Day Record'}
            </h3>
            <p className="text-[9px] text-foreground/40 font-mono tracking-widest uppercase mt-0.5">
              Instrument Parameter Adjustment
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-foreground/45 hover:text-foreground transition-colors p-1 rounded hover:bg-foreground/5 cursor-pointer"
          >
            <svg className="h-4 w-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="space-y-4">
          {/* Date Parameter */}
          <div className="space-y-1">
            <label className="text-[9px] text-foreground/50 font-mono uppercase tracking-wider block">
              Log Date
            </label>
            {isNew ? (
              <input
                type="date"
                required
                value={selectedDate}
                onChange={(e) => setSelectedDate(e.target.value)}
                className="w-full bg-background border border-card-border focus:border-accent/40 rounded px-3 py-2 text-xs font-mono text-foreground focus:outline-none transition-all"
              />
            ) : (
              <div className="w-full bg-background/30 border border-card-border/50 rounded px-3 py-2 text-xs font-mono text-foreground/60 select-none">
                {date}
              </div>
            )}
          </div>

          {/* Hours Parameter */}
          <div className="space-y-1">
            <label className="text-[9px] text-foreground/50 font-mono uppercase tracking-wider block">
              Daily Hours Worked
            </label>
            <input
              type="number"
              step="any"
              min="0"
              max="24"
              required
              value={hours}
              onChange={(e) => setHours(e.target.value)}
              className="w-full bg-background border border-card-border focus:border-accent/40 rounded px-3 py-2 text-xs font-mono text-foreground focus:outline-none transition-all"
              placeholder="e.g. 8.00"
            />
          </div>

          {/* Status Parameter */}
          <div className="space-y-1.5">
            <label className="text-[9px] text-foreground/50 font-mono uppercase tracking-wider block">
              Duty Status Parameter
            </label>
            <input
              type="text"
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="w-full bg-background border border-card-border focus:border-accent/40 rounded px-3 py-2 text-xs font-mono text-foreground focus:outline-none transition-all"
              placeholder="e.g. Present"
            />
            {/* Quick selectors */}
            <div className="flex flex-wrap gap-1 pt-1">
              {quickStatuses.map((qs) => (
                <button
                  key={qs}
                  type="button"
                  onClick={() => setStatus(qs)}
                  className={`px-2 py-0.5 border rounded-full text-[8px] font-mono transition-all cursor-pointer ${
                    status.toLowerCase() === qs.toLowerCase()
                      ? 'bg-accent/10 border-accent text-accent'
                      : 'border-card-border hover:border-foreground/20 text-foreground/50 hover:text-foreground'
                  }`}
                >
                  {qs}
                </button>
              ))}
            </div>
          </div>

          {/* Actions */}
          <div className="flex justify-between items-center gap-2 pt-3 border-t border-card-border/50">
            {!isNew && (initialHours > 0 || initialStatus !== '') ? (
              <button
                type="button"
                onClick={handleDelete}
                className="px-4 py-2 bg-red-950/10 hover:bg-red-500/10 border border-red-500/20 rounded text-[10px] font-mono uppercase tracking-wider font-semibold text-red-400 transition-all cursor-pointer"
              >
                Clear Record
              </button>
            ) : (
              <div />
            )}

            <div className="flex items-center gap-2">
              <button
                type="button"
                onClick={onClose}
                className="px-4 py-2 border border-card-border text-foreground/60 hover:text-foreground bg-background/10 hover:bg-background/25 rounded text-[10px] font-mono uppercase tracking-wider font-semibold transition-all cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-accent hover:bg-accent/90 border border-accent/20 rounded text-[10px] font-mono uppercase tracking-wider font-semibold text-foreground transition-all cursor-pointer"
              >
                Save Parameter
              </button>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default DayEditModal;
